import * as React from "react";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { observer } from "mobx-react";

const buttonVariantsBase = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding text-xs/relaxed font-medium whitespace-nowrap transition-[color,background-color,border-color,box-shadow,transform] duration-(--motion-duration-fast) outline-none select-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        /**
         * `border-border/60`, not the full token: at 1px, `--border` #c3c7cc is
         * the heaviest mark in most rows it appears in, and it lands on the
         * secondary action rather than the primary one. The softer hairline
         * keeps the box — and with it the affordance — while letting whatever
         * is filled nearby do the contrasting.
         *
         * One value covers both themes because `--border` is already
         * `oklch(1 0 0 / 10%)` in dark and the opacity modifier mixes rather
         * than replaces, so `/60` resolves to 6% white there on its own.
         */
        outline:
          "border-border/60 bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-7 gap-1 px-2 text-xs/relaxed has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        xs: "h-5 gap-1 rounded-sm px-2 text-[0.625rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-2.5",
        sm: "h-6 gap-1 px-2 text-xs/relaxed has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        lg: "h-8 gap-1 px-2.5 text-xs/relaxed has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-4",
        chip: "h-6 gap-1 rounded-full px-2 text-xs/relaxed has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        icon: "size-7 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-xs": "size-5 rounded-sm [&_svg:not([class*='size-'])]:size-2.5",
        "icon-sm": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-lg": "size-8 [&_svg:not([class*='size-'])]:size-4",
      },
      /**
       * A colour for a ghost button that stands for a state: an overdue count,
       * an over-capacity warning. It colours the label at rest and keeps the
       * hover in the same hue, replacing the ghost's neutral `muted` hover in
       * both themes (10% of the tone in light, 16% in dark). Ghost only: the
       * other variants carry their own fill, so a tone there is ignored.
       */
      tone: {
        warning: "",
        destructive: "",
      },
    },
    compoundVariants: [
      {
        variant: "ghost",
        tone: "warning",
        className:
          "text-warning hover:bg-warning/10 hover:text-warning aria-expanded:bg-warning/10 aria-expanded:text-warning dark:hover:bg-warning/16",
      },
      {
        variant: "ghost",
        tone: "destructive",
        className:
          "text-destructive hover:bg-destructive/10 hover:text-destructive aria-expanded:bg-destructive/10 aria-expanded:text-destructive dark:hover:bg-destructive/16",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariantsBase>;

/**
 * The Button classes for a variant, size and tone, already merged.
 *
 * Merged so a later class replaces the one it conflicts with: a tone's
 * `hover:bg-destructive/10` has to remove the ghost's `hover:bg-muted`, because
 * the generated stylesheet emits `hover:bg-muted` after it and would let the
 * neutral hover win if both stayed on the element. A call site that puts these
 * classes on a non-Button element, such as a router `Link`, gets what `Button`
 * renders.
 */
function buttonVariants(props?: Parameters<typeof buttonVariantsBase>[0]): string {
  return cn(buttonVariantsBase(props));
}

const Button = observer(
  React.forwardRef<
    React.ElementRef<typeof ButtonPrimitive>,
    ButtonPrimitive.Props & ButtonVariantProps
    // eslint-disable-next-line mobx/missing-observer -- outer observer() wraps the forwardRef
  >(function Button({ className, variant = "default", size = "default", tone, ...props }, ref) {
    return (
      <ButtonPrimitive
        ref={ref}
        data-slot="button"
        className={buttonVariants({ variant, size, tone, className })}
        {...props}
      />
    );
  }),
);

Button.displayName = "Button";

export { Button, buttonVariants };
